import { Link } from "react-router-dom";
import Footer from "../components/Footer";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* Modern Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h4" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900 tracking-tight">Prindox</span>
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-2xl mb-6 text-white">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">Terms of Service</h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Please read these terms carefully before using Prindox.
          </p>
          <p className="text-sm font-medium text-gray-500 mt-6 bg-white inline-block px-4 py-2 rounded-full border border-gray-200">Last updated: December 2024</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-gray mx-auto">
            
            <div className="bg-gray-50 border-l-4 border-gray-900 p-6 rounded-r-xl mb-12">
              <h3 className="text-gray-900 font-bold mt-0">Agreement to Terms</h3>
              <p className="text-gray-700 mb-0">
                By accessing or using Prindox, you agree to be bound by these Terms of Service. 
                If you do not agree with any of these terms, you are prohibited from using this service.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Service Description</h2>
            <ul className="text-gray-600 space-y-3 mb-8 list-none pl-0">
              <li className="flex items-start"><span className="mr-3 text-blue-500 mt-1">✓</span> Document upload and transfer via QR code</li>
              <li className="flex items-start"><span className="mr-3 text-blue-500 mt-1">✓</span> Secure, temporary document storage</li>
              <li className="flex items-start"><span className="mr-3 text-blue-500 mt-1">✓</span> Real-time notifications</li>
              <li className="flex items-start"><span className="mr-3 text-blue-500 mt-1">✓</span> Shop management tools</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">User Responsibilities</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  For Document Senders
                </h3>
                <ul className="text-gray-600 text-sm space-y-2 list-none pl-0">
                  <li>• Only upload content you have rights to</li>
                  <li>• No illegal or harmful content</li>
                  <li>• Respect intellectual property</li>
                  <li>• Pay applicable shop fees</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  For Shop Owners
                </h3>
                <ul className="text-gray-600 text-sm space-y-2 list-none pl-0">
                  <li>• Provide accurate business info</li>
                  <li>• Maintain fair pricing</li>
                  <li>• Protect customer privacy</li>
                  <li>• Comply with local laws</li>
                </ul>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Prohibited Content</h2>
            <div className="bg-red-50 p-6 rounded-xl mb-8 border border-red-100">
              <p className="text-red-800 font-medium mb-4">You may not upload:</p>
              <ul className="text-red-700 space-y-2 list-disc pl-5">
                <li>Illegal, harmful, threatening, or harassing content</li>
                <li>Content infringing on intellectual property rights</li>
                <li>Malware, viruses, or malicious code</li>
                <li>Pornographic or obscene material</li>
                <li>Confidential information without authorization</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              To the maximum extent permitted by law, Prindox shall not be liable for:
            </p>
            <ul className="text-gray-600 space-y-2 mb-8 list-none pl-0">
              <li className="flex items-start"><span className="mr-2 text-gray-400">•</span> Indirect, incidental, or consequential damages</li>
              <li className="flex items-start"><span className="mr-2 text-gray-400">•</span> Loss of data or business opportunities</li>
              <li className="flex items-start"><span className="mr-2 text-gray-400">•</span> Actions of participating print shops</li>
              <li className="flex items-start"><span className="mr-2 text-gray-400">•</span> Service interruptions</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Contact Information</h2>
            <div className="bg-gray-100 p-8 rounded-2xl">
              <p className="mb-4 text-gray-700 font-medium">For legal inquiries or support:</p>
              <div className="space-y-2">
                <p className="text-gray-600">📧 <a href="mailto:legal@scan2print.com" className="text-blue-600 hover:underline ml-2">legal@scan2print.com</a></p>
                <p className="text-gray-600">🆘 <a href="mailto:support@scan2print.com" className="text-blue-600 hover:underline ml-2">support@scan2print.com</a></p>
              </div>
            </div>

            <div className="mt-12 text-sm text-gray-500 text-center">
              <p>By using Prindox, you acknowledge that you have read, understood, and agree to be bound by these Terms.</p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}